<script setup lang="ts">
import { ref } from "vue";
import { Check, Hash } from "@lucide/vue";
import { useToast } from "vue-toastification";

import { useStepHeadingLevel, useStepIsPageSection, useStepSectionId } from "../headingLevel";

withDefaults(
  defineProps<{
    /** What the step is asking for, phrased as a question. Never the rail's label, which is already on screen. */
    heading: string;
    /** One short line of guidance. Left out entirely when there is nothing worth saying. */
    hint?: string;
  }>(),
  { hint: undefined }
);

/**
 * `h1` on a wizard route, where the step is the page. A host that renders several steps at once
 * provides `h2` instead, and the cards inside the step follow one level below — see headingLevel.ts.
 */
const headingLevel = useStepHeadingLevel();

/**
 * On a host that renders every step at once, this heading is a section header on a long scroll, so
 * it pins to the top of the scroll container for the length of its own section.
 *
 * Sticky is constrained by the element's PARENT, which is what makes this work without the page
 * knowing anything: a step component's root spans its whole step, so the band travels the section
 * and is pushed out by the next section's band. It is the ROOT here that sticks, not an inner row —
 * an inner row's containing block is this small intro block and it would unpin almost immediately.
 *
 * `bg-slate-0` is the settings page's content ground, so the cards pass underneath invisibly, and
 * `z-sticky` is the token for exactly this (never a raw `z-10`). It tracks that page's canvas: while
 * the canvas was briefly slate-50 this band was too, and a band that does not match paints a grey
 * strip across the top of a white page as soon as it pins.
 *
 * The negative `top` is not a nudge. A sticky element pins to the scroll container's PADDING box,
 * and `PageContentWrapper` carries `pt-6` — so `top-0` parked the band 24px below the container's
 * top edge and left a strip above it where the outgoing section's cards showed through, clipped, as
 * if a row had come loose. `-top-6` cancels exactly that padding, so the band pins flush to the
 * container edge and covers what passes under it. It has no effect at all until the band pins,
 * since `top` applies only while an element is stuck.
 *
 * Which is why the top padding is 36px and not 12px: the 24px the negative `top` pulls up is
 * clipped by the container, so whatever sits in the band's first 24px is invisible while pinned.
 * With `py-3` that was the top half of the heading's own glyphs — the section title was cut in
 * half for the entire length of its section, which is most of the reason it was hard to tell which
 * fields belonged to which section. 36px = the 24px that gets eaten plus the 12px of breathing
 * room the band should actually have, so the pinned title reads exactly as it does in flow.
 *
 * `border-t` for the same question's other half, where a section BEGINS: a rule above the heading,
 * with the band's existing rule below it, turns the heading into a header strip between the section
 * that ended and the fields that follow. It is inside the clipped 24px while pinned, which is
 * correct — by then the previous section is off-screen and there is nothing left to divide.
 */
const isPageSection = useStepIsPageSection();

/**
 * The fragment this section answers to, from the host (see `SettingsSection.vue`). Empty on a wizard
 * route, where the step is the whole page and there is nothing to link PAST.
 */
const sectionId = useStepSectionId();

const toast = useToast();

/** Flips the icon for a moment, because the toast appears in a corner and the click happened here. */
const wasCopied = ref(false);

/**
 * Copies a link to this section — the page's own URL plus the fragment — so it can be pasted into a
 * message and land the reader on this heading. Deliberately does NOT navigate or scroll: the reader
 * is already here, and pushing a hash would move the view and add a history entry for a copy.
 *
 * Read off `window.location` rather than `useRoute()`, which would make this shared component
 * require a router to render at all — it appears in eleven step stories that provide none, and
 * `useRoute()` there warns on setup and would throw on the click. `pathname` is the route's path
 * because the app is on `createWebHistory`, and taking it without `search`/`hash` is also what stops
 * a link copied from `…/settings#transport` coming out as `…/settings#transport#installation`.
 */
const copySectionLink = async () => {
  const url = `${window.location.origin}${window.location.pathname}#${sectionId}`;

  try {
    await navigator.clipboard.writeText(url);
    wasCopied.value = true;
    toast.success("Link to this section copied");
    window.setTimeout(() => (wasCopied.value = false), 2000);
  } catch {
    // The Clipboard API needs a secure context and a permission that can be refused, so a failure
    // here is a real possibility rather than a defensive branch — and a copy that silently did
    // nothing is worse than one that says so.
    toast.error("Could not copy the link");
  }
};
</script>

<!--
  The heading and guidance at the top of a wizard step screen.

  The heading is the page's `h1` on a wizard route. Nothing else there claims one: the sidebar's group labels
  are `h2`/`h3` and the step screens print `h2` section titles, so before this the page had no level-one heading
  at all. Sections inside a step stay `h2` underneath it.

  A host that renders SEVERAL steps on one page — the project's settings page — provides `h2` through
  `stepHeadingLevelKey`, because seven `h1`s on one document is no outline at all. See that file.

  A plain line rather than a filled box, because the hint sits above six to thirteen inputs here — a box per
  step reads as furniture at that density. Two screens already used exactly this line, so this standardises
  them rather than adding a third idiom. Long-form guidance with tables stays in its own panel, which today
  means B6EnergyFrameGuidance.

  A string prop and not a slot for the hint: the paragraph would otherwise wrap whatever a caller passes, and
  nesting a `p` inside a `p` is invalid. The `action` slot exists because three screens carry an
  InfoModalButton beside their title.

  It owns no outer spacing on purpose. Some step screens are a single wrapper with a grid inside, where this
  needs a margin; others render several root elements into the wizard's `space-y-12` column, where a margin of
  its own would stack on top of 48px of gap. So the caller passes the spacing, the way `col-span-*` is passed
  to inputs throughout these screens.
-->
<template>
  <div :class="isPageSection ? 'z-sticky bg-slate-0 sticky -top-6 border-y border-slate-200 pt-9 pb-3' : undefined">
    <div class="flex items-center gap-2">
      <component
        :is="headingLevel"
        class="t-sh3 text-type-default"
        >{{ heading }}</component
      >

      <!-- Rendered only where a section id exists, which is only on a host that lays several steps
           out as one document — a wizard route has nothing to link past.

           Quiet but always there rather than revealed on hover: seven bright hash marks down a page
           is clutter, and hover-only would put it out of reach on touch and out of sight for anyone
           who never happens to hover a heading. -->
      <!-- No tooltip: `aria-label` still names it for a screen reader, and the icon plus the green
           check it flips to say the rest. -->
      <button
        v-if="sectionId"
        type="button"
        class="focus-ring interactive-active rounded-4 duration-fast ease-standard shrink-0 p-1 opacity-60 transition-all hover:bg-slate-100 hover:opacity-100"
        aria-label="Copy link to this section"
        @click="copySectionLink">
        <Check
          v-if="wasCopied"
          class="icon-sm text-app-green-600"
          :stroke-width="2"
          aria-hidden="true" />
        <Hash
          v-else
          class="icon-sm text-app-blue-600"
          :stroke-width="1.5"
          aria-hidden="true" />
      </button>

      <slot name="action" />
    </div>

    <p
      v-if="hint"
      class="t-small text-type-muted mt-2">
      {{ hint }}
    </p>
  </div>
</template>
